<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { YieldQueries } from '@/features/assets/queries'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons'
import BadgeYellow from '@/components/badge-yellow.vue'
import BadgeTeal from '@/components/badge-teal.vue'
import BadgeRed from '@/components/badge-red.vue'
import BadgePrimary from '@/components/badge-primary.vue'
import { computed } from 'vue'

const props = defineProps<{ assetCode: string }>()
const { data, isError, isFetching } = useQuery(YieldQueries.fetch(() => props.assetCode))

const phase = computed(
  () => {
    switch (data.value?.phase) {
      case 'warranty':
        return 'Warranty/AMA'
      case 'guarantee':
        return 'Guarantee'
      default:
        return undefined
    }
  }
)

const state = computed(
  () => {
    if (!data.value) {
      return undefined
    }
    if (data.value.status === 'approved') {
      return { component: BadgeTeal, text: 'Approved' }
    }
    if (data.value.status === 'rejected') {
      return { component: BadgeRed, text: 'Rejected' }
    }
    if (data.value.status === 'approving') {
      const approvals = data.value.approvals
      const suffix = approvals.requiredCount > 1
        ? ` (${approvals.received.length}/${approvals.requiredCount})`
        : ''

      return { component: BadgeYellow, text: `Pending Approval${suffix}` }
    }
    return undefined
  }
)
</script>

<template>
  <div class="d-flex">
    <div class="d-flex align-items-center">
      <BadgeRed :class="$style.status" v-if="isError">Error in fetching status</BadgeRed>

      <template v-else>
        <div v-if="phase">
          <span class="fw-bold text-uppercase me-3">Yield Phase</span>
          <BadgePrimary :class="$style.status" v-if="phase">
            <FontAwesomeIcon v-if="isFetching || data?.hasPendingUpdate" :icon="faSpinnerThird" spin />
            <template v-else>{{ phase }}</template>
          </BadgePrimary>
        </div>

        <div class="ms-4" v-if="state">
          <span class="fw-bold text-uppercase me-3">Status</span>
          <component :class="$style.status" :is="state.component">
            <FontAwesomeIcon v-if="isFetching || data?.hasPendingUpdate" :icon="faSpinnerThird" spin />
            <template v-else>{{ state.text }}</template>
          </component>
        </div>
      </template>
    </div>
  </div>
</template>

<style module lang="scss">
.status {
  min-width: 149px;
}
</style>
